// colors will be an object who's keys are the formatted string values of the group/category/state names as they appear in the interface.
/**
 *  example colors = {
 *    'production uptime': '#FF0000',
 *    idle: '#00FF00',
 *  }
 */

import { styledTheme } from 'common/theme';
import theme from 'themes';
import { CleaningStepCategories, ProteinMachineState } from 'types/protein';

const baseColors = {
  undefined: styledTheme.colors.Telemetry700,
  'running': styledTheme.colors.success200,
  'idle': theme.colors.machineUtilizationColors.idle,
  'cleaning': styledTheme.colors.Telemetry300,
  'paused': styledTheme.colors.Telemetry600,
  'stop': styledTheme.colors.offline,
  'waitingToBeSelected': styledTheme.colors.Telemetry500,
  'SelectedWaitingToBeStarted': styledTheme.colors.Telemetry2300,
  'StartingUp': styledTheme.colors.Telemetry1700,
  'ShutDownActive': styledTheme.colors.Telemetry1900,
  'alarm': styledTheme.colors.warning,
  'online': styledTheme.colors.running,
  'connected': styledTheme.colors.Telemetry1000,
  'disconnected': styledTheme.colors.error200,
  'unknown': styledTheme.colors.error200,
  'not running': styledTheme.colors.darkRed,
  'stop by alarm': styledTheme.colors.warning,
  'stop_alarm': styledTheme.colors.warning,
  'restore': styledTheme.colors.Telemetry800,
  'transition': styledTheme.colors.Telemetry900,
  'forced_cooling': styledTheme.colors.Telemetry400,
  'passive_cooling': styledTheme.colors.Telemetry1100,
  'emergency_cooling': styledTheme.colors.error200,
  'CriticalAlarm': '#9232A0',
  'Warning': '#FFA019',
  'PauseInProgress': '#325FA0',
  'BeltStopped':  styledTheme.colors.darkRed,
  'Drum1Jog': styledTheme.colors.Telemetry1200,
  'Drum2Jog': styledTheme.colors.Telemetry1300,
  'Ramping': styledTheme.colors.Telemetry1400,
  'RampDown':  styledTheme.colors.Telemetry1500,
  'DrumsRunFree': styledTheme.colors.Telemetry1600,
  'Setting': styledTheme.colors.Telemetry1700,
  'service': theme.colors.machineUtilizationColors.service
};

const stepOverColors: Record<string, string> = {
  'Drainage of nozzles': 'rgba(0, 118, 204, 1)',
  'Air purge of circuits before drying': 'rgba(138, 136, 134, 1)',
  'Defrost inspection': 'rgba(0, 158, 179, 1)',
  'Roller chain rinse': 'rgba(182, 44, 16, 1)',
  'Foaming of belt, evaporator and equipment': '#00cc99',
  'Belt rinse': '#29A429',
  'Hot gas': 'rgb(105, 193, 150)',
  'Evaporator defrost': 'rgb(234, 118, 0)',
  'Defrost inspection middle': '#e91e63',
  'Equipment defrost/rinse': 'rgba(138, 136, 134, 1)',
  'Mist rinse': '#c135d4',
  'Foaming inspection': 'rgba(131, 93, 208, 1)',
  'Equipment defrost': '#5e3b27',
  'Evaporator rinse': 'rgba(255, 173, 193, 1)',
  'Rinse inspection end': 'rgba(252, 214, 65, 1)',
  'Cleaning detergent nozzles': 'rgba(125, 150, 195, 1)',
  'Rinse inspection middle': '#d43552',
  Completed: '#8c1aff',
  Complete: '#8c1aff',
  Other: '#000000',
  Rinse: '#0E00B3',
  Defrost: '#2E8FFF',
  Filling: '#708BBD',
  Inspection: '#49986C',
  Disinfection: '#835DD0',
  Drying: '#DC3240',
  'Manual Pause': '#C83D95',
  Evaporator: '#A05B32',
  'Invalid Group': '#E66C37',
  Cooldown: '#126CFF',
  'Pre rinse': '#8A8886',
  Chemical: '#FCD641'
};

const recipeColors: Record<string, string> = {
  'chicken nuggets *': 'rgba(230, 108, 55, 1)',
  'full defrost and clean': 'rgba(0, 118, 204, 1)',
  drying: 'rgba(138, 136, 134, 1)',
  'belt rinse test': 'rgba(0, 158, 179, 1)',
  'no selection': 'rgba(182, 44, 16, 1)',
  'BK Tempura Test 05Oct': '#00cc99',
  rinse: 'rgb(105, 193, 150)',
  'BK Tempura Test 05Oct *': 'rgb(234, 118, 0)',
  'ww 2': '#e91e63',
  'ww 2 *': '#c135d4',
  'crispy fillet': 'rgba(131, 93, 208, 1)',
  'crispy fillet *': '#5e3b27',
  'bk wings': 'rgba(255, 173, 193, 1)',
  'bk long': 'rgba(252, 214, 65, 1)',
  'manual cleaning': 'rgba(180, 241, 155, 1)',
  'bk fc value burger': '#d43552',
  'crispy strips': '#8c1aff',
  '18.polbagietka *': '#29A429',
  '18.polbagietka': '#2A629A',
  'no Selection': '#29A429'
};

export const machineUtilizationColors = {
  run: baseColors.running,
  clean: baseColors.cleaning,
  idle: baseColors.idle,
  paused: baseColors.paused,
  stopByAlarm: baseColors.stop
};

export const proteinMachineStateColors = {
  [ProteinMachineState.Idle]: baseColors.idle,
  [ProteinMachineState.WaitingTobeSelected]: baseColors.waitingToBeSelected,
  [ProteinMachineState.SelectedWaitingToBeStarted]: baseColors.SelectedWaitingToBeStarted,
  [ProteinMachineState.StartingUp]: baseColors.StartingUp,
  [ProteinMachineState.Running]: baseColors.running,
  [ProteinMachineState.PausedByOperatorOrExternal]: baseColors.paused,
  [ProteinMachineState.Stop]: baseColors.stop,
  [ProteinMachineState.ShutDownActive]: baseColors.ShutDownActive,
  [ProteinMachineState.CompletedWaitingForOperator]: baseColors.cleaning,
  [ProteinMachineState.Alarm]: baseColors.alarm,
  [ProteinMachineState.CriticalAlarm]: baseColors.CriticalAlarm,
  [ProteinMachineState.Warning]: baseColors.Warning,
  [ProteinMachineState.PauseInProgress]: baseColors.PauseInProgress,
  [ProteinMachineState.BeltStopped]: baseColors.BeltStopped,
  [ProteinMachineState.Drum1Jog]: baseColors.Drum1Jog,
  [ProteinMachineState.Drum2Jog]: baseColors.Drum2Jog,
  [ProteinMachineState.Ramping]: baseColors.Ramping,
  [ProteinMachineState.RampDown]: baseColors.RampDown,
  [ProteinMachineState.DrumsRunFree]: baseColors.DrumsRunFree,
  [ProteinMachineState.Setting]: baseColors.Setting
};

export const MachineStateColors = {
  '0': baseColors.idle,
  '1': baseColors.running,
  '2': baseColors.cleaning,
  '3':'#694085',
  '4': baseColors.paused,
  '5': baseColors.stop,
  '6': baseColors.restore,
  '7': baseColors.transition,
  '8': baseColors.forced_cooling,
  '9': baseColors.passive_cooling,
  '10': baseColors.emergency_cooling,
  '11': baseColors.service
};

export const cleaningStepColors = {
  [CleaningStepCategories.Rinse]: styledTheme.colors.Telemetry100,
  [CleaningStepCategories.Defrost]: styledTheme.colors.Telemetry500,
  [CleaningStepCategories.Filling]: styledTheme.colors.Telemetry600,
  [CleaningStepCategories.Inspection]: styledTheme.colors.Telemetry1200,
  [CleaningStepCategories.Disinfection]: styledTheme.colors.Telemetry800,
  [CleaningStepCategories.Drying]: styledTheme.colors.Telemetry1400,
  [CleaningStepCategories.Detergent]: '#265CD9',
  [CleaningStepCategories.Complete]: '#8c1aff',
  [CleaningStepCategories.UndefinedSequence]: '#2F446C',
  [CleaningStepCategories.EvaporatorEquipmentDefrostRinse]: '#D92665',
  [CleaningStepCategories.EvaporatorDefrost]: '#89105D',
  [CleaningStepCategories.ConveyorDefrost]: '#56B2A1',
  [CleaningStepCategories.InvalidGroup]: styledTheme.colors.Telemetry1700,
  [CleaningStepCategories.Cooldown]: styledTheme.colors.Telemetry400,
  [CleaningStepCategories.PreRinse]: styledTheme.colors.Telemetry2100,
  [CleaningStepCategories.Chemical]: styledTheme.colors.Telemetry2300,
  [CleaningStepCategories.Neutralization]: styledTheme.colors.Telemetry200,
  [CleaningStepCategories.Sanitation]: styledTheme.colors.Telemetry1800,
  [CleaningStepCategories.Undefined]: styledTheme.colors.Telemetry2200,
  [CleaningStepCategories.Manual]: styledTheme.colors.Telemetry1000,
  [CleaningStepCategories.Caustic]: styledTheme.colors.Telemetry700,
  [CleaningStepCategories.SteamSanitize]: styledTheme.colors.Telemetry900,
};

export const driveTorqueRatioColors = {
  inner: '#BEF264',
  outer: '#67E8F9'
}

export const colors: Record<string, string> = {
  alarm: 'rgba(230, 108, 55, 1)',
  'critical alarm': 'rgba(0, 118, 204, 1)',
  'warning information': 'rgba(138, 136, 134, 1)',
  undefined: '#000000',
  ...baseColors,
  ...stepOverColors,
  ...recipeColors,
  ...driveTorqueRatioColors
};
